<template>
  <div>
    <template v-if="hasTemplateConfig">
      <a-alert
        v-if="isManualMode && !readOnly"
        type="warning"
        show-icon
        style="margin-bottom: 1rem;">
        <template #message>
          Ручной режим JSON
        </template>
        <template #description>
          JSON был загружен из планировки или изменен вручную. Поля формы не
          перезаписывают редактор, пока вы явно не пересоберете конфиг.
          <a-button
            type="link"
            size="small"
            @click="rebuildFromForm">
            Пересобрать из формы
          </a-button>
        </template>
      </a-alert>

      <a-row :gutter="[16, 0]">
        <a-col
          v-for="field in visibleFields"
          :key="field.id"
          :span="field.span || 12">
          <a-form-item
            :label="field.label"
            :required="field.required">
            <a-select
              v-if="field.type === 'select'"
              v-model:value="renderForm[field.id]"
              :options="getFieldOptions(field)"
              :disabled="isManualMode || readOnly"
              :show-search="field.showSearch"
              :allow-clear="field.allowClear"
              option-filter-prop="label"
              :placeholder="field.placeholder" />
            <a-input
              v-if="field.type === 'text'"
              v-model:value="renderForm[field.id]"
              :disabled="isManualMode || readOnly"
              :placeholder="field.placeholder" />
            <a-input-number
              v-if="field.type === 'number'"
              v-model:value="renderForm[field.id]"
              :disabled="isManualMode || readOnly"
              :min="field.min"
              :max="field.max"
              style="width: 100%;" />
            <a-switch
              v-if="field.type === 'switch'"
              v-model:checked="renderForm[field.id]"
              :disabled="isManualMode || readOnly" />
            <a-select
              v-if="field.type === 'multiselect'"
              :value="renderForm[field.id]"
              :options="getFieldOptions(field)"
              :disabled="isManualMode || readOnly"
              :show-search="field.showSearch"
              :allow-clear="field.allowClear"
              mode="multiple"
              option-filter-prop="label"
              :placeholder="field.placeholder"
              @change="values => handleMultipleFieldChange(field, values)" />
            <a-checkbox-group
              v-if="field.type === 'checkbox-group'"
              :value="renderForm[field.id]"
              :disabled="isManualMode || readOnly"
              :options="getFieldOptions(field)"
              @change="values => handleMultipleFieldChange(field, values)" />
            <template v-if="getFileSourceSettings(field)">
              <div style="margin-top: 8px;">
                <div>Источник загрузки</div>
                <a-select
                  :value="fileSourceSelections[field.id]?.source || 'backend'"
                  :options="getFileSourceOptions(field)"
                  :aria-label="`${field.label || field.id}: источник загрузки`"
                  :disabled="isManualMode || readOnly"
                  style="width: 100%;"
                  @update:value="value => setFileSource(field, value)" />
                <a-input
                  v-if="fileSourceSelections[field.id]?.source === 'custom'"
                  :value="fileSourceSelections[field.id].customDomain"
                  :aria-label="`${field.label || field.id}: домен загрузки`"
                  :disabled="isManualMode || readOnly"
                  placeholder="https://cdn.example.com"
                  style="margin-top: 8px;"
                  @update:value="value => setCustomFileDomain(field, value)" />
              </div>
            </template>
          </a-form-item>
        </a-col>
      </a-row>

      <a-collapse>
        <a-collapse-panel header="Конфиг GLTF">
          <monaco-editor
            :model-value="gltfConfigText"
            language="json"
            :read-only="readOnly"
            :style="{ height: editorHeight }"
            @update:model-value="handleEditorUpdate"
            @update:has-errors="handleJsonErrorsUpdate" />
        </a-collapse-panel>
      </a-collapse>
    </template>

    <monaco-editor
      v-else
      :model-value="gltfConfigText"
      language="json"
      :read-only="readOnly"
      :style="{ height: editorHeight }"
      @update:model-value="handleEditorUpdate"
      @update:has-errors="handleJsonErrorsUpdate" />
  </div>
</template>

<script setup>
import {
  computed, nextTick, onMounted, reactive, ref, watch,
} from 'vue';
import { useStore } from 'vuex';
import appConfig from '@/config'; // eslint-disable-line import/extensions
import MonacoEditor from '@/Components/Common/MonacoEditor.vue';
import { getEnvironmentDisplayName } from '@/helpers/environmentDisplayName';
import {
  normalizeDownloadBaseUrl, replaceDownloadBaseUrl, restoreDownloadSource,
} from '@/utils/fileDownloadSource';
import {
  enforceExclusiveCatalogGroups,
  extractGltfRenderTemplateParameters,
  isGltfRenderConditionMatched,
  resolveFieldRuntimeDefaults,
  resolveGltfRenderTemplate,
} from '@/utils/resolveGltfRenderTemplate';

const props = defineProps({
  modelValue: {
    type: [String, Object],
    default: '',
  },
  plan: {
    type: Object,
    default: () => ({}),
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  editorHeight: {
    type: String,
    default: '320px',
  },
});

const emit = defineEmits(['update:modelValue', 'update:hasErrors']);

const store = useStore();
const gltfRenderTemplateConfig = appConfig.modules?.render_gltf || null;
const hasTemplateConfig = computed(() => Boolean(gltfRenderTemplateConfig));

const clone = (value) => JSON.parse(JSON.stringify(value));
const renderForm = reactive(clone(gltfRenderTemplateConfig?.defaults || {}));
const fileSourceSelections = reactive({});
const gltfConfigText = ref('');
const hasJsonErrors = ref(false);
const isApplyingGeneratedConfig = ref(false);
const isResettingData = ref(false);
const isManualMode = ref(false);
const programmaticConfigText = ref(null);

const toOptions = (catalog) => Object.entries(catalog)
  .map(([value, item]) => ({ label: item.label, value }));

const normalizeConfigToString = (config) => {
  if (config === null || config === undefined) return '';
  if (typeof config === 'string') return config;
  try {
    return JSON.stringify(config, null, 2);
  } catch (e) {
    return '';
  }
};

const stableStringify = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }

  return JSON.stringify(value);
};

const parseJsonConfig = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const styleOptions = computed(() => (store.state.substances.stylesList || [])
  .map((style) => ({ label: style.name, value: style.id })));

const environmentMapOptions = computed(() => (store.state.substances.unrealEnvironment || [])
  .filter((environment) => environment.deleted_at === null
    && environment.type === 'web'
    && environment.sky_interior_cubemap_url)
  .map((environment) => ({
    label: getEnvironmentDisplayName(environment),
    value: environment.sky_interior_cubemap_url,
  })));

const getFileSourceSettings = (field) => {
  if (field.fileSource === false) return null;
  if (!field.fileSource && field.optionsSource !== 'webEnvironmentMaps') return null;

  const settings = {
    backendBaseUrl: `${appConfig.apiUrl.replace(/\/+$/, '')}/storage`,
    cdnPathPrefix: '/app_storage_production/public',
    ...gltfRenderTemplateConfig.fileSources,
    ...(typeof field.fileSource === 'object' ? field.fileSource : {}),
  };
  settings.backendBaseUrl = normalizeDownloadBaseUrl(settings.backendBaseUrl);
  settings.cdns = (settings.cdns || []).map((cdn) => ({
    ...cdn,
    baseUrl: normalizeDownloadBaseUrl(cdn.url, settings.cdnPathPrefix),
  })).filter((cdn) => cdn.baseUrl && cdn.id && !['backend', 'custom'].includes(cdn.id));
  return settings;
};

const getFileSourceOptions = (field) => [
  { label: 'Бэкенд (оригинал)', value: 'backend' },
  ...getFileSourceSettings(field).cdns.map((cdn) => ({ label: cdn.label || cdn.url, value: cdn.id })),
  { label: 'Другой домен', value: 'custom' },
];

const setFileSource = (field, source) => {
  fileSourceSelections[field.id] = {
    customDomain: '', ...fileSourceSelections[field.id], source,
  };
};

const setCustomFileDomain = (field, customDomain) => {
  fileSourceSelections[field.id] = { source: 'custom', customDomain };
};

const getSelectedFileBase = (field) => {
  const settings = getFileSourceSettings(field);
  const selection = fileSourceSelections[field.id];
  if (!selection || selection.source === 'backend') return settings.backendBaseUrl;
  if (selection.source === 'custom') {
    return normalizeDownloadBaseUrl(selection.customDomain, settings.cdnPathPrefix, true);
  }
  return settings.cdns.find((cdn) => cdn.id === selection.source)?.baseUrl;
};

const getRenderTemplate = () => {
  const template = clone(gltfRenderTemplateConfig.template);
  const hasEnvironmentSource = gltfRenderTemplateConfig.fields.some((field) => (
    field.optionsSource === 'webEnvironmentMaps' && getFileSourceSettings(field)
  ));
  if (hasEnvironmentSource && template.widget) {
    const widget = template.widget.$value || template.widget;
    widget.env_path_template = '{output_file}';
  }
  return template;
};

function getGeneratedConfig() {
  const parameters = clone(renderForm);
  gltfRenderTemplateConfig.fields.forEach((field) => {
    const settings = getFileSourceSettings(field);
    if (!settings) return;
    const targetBase = getSelectedFileBase(field);
    parameters[field.id] = replaceDownloadBaseUrl(parameters[field.id], settings.backendBaseUrl, targetBase);
  });
  return resolveGltfRenderTemplate(getRenderTemplate(), parameters, gltfRenderTemplateConfig.catalogs);
}

const visibleFields = computed(() => (gltfRenderTemplateConfig?.fields || [])
  .filter((field) => isGltfRenderConditionMatched(field.visibleWhen, renderForm)));

const getFieldOptions = (field) => {
  if (field.options) {
    return field.options.map((value) => ({ label: value, value }));
  }
  if (field.optionsCatalog) {
    return toOptions(gltfRenderTemplateConfig.catalogs[field.optionsCatalog] || {});
  }
  if (field.optionsSource === 'styles') {
    return styleOptions.value;
  }
  if (field.optionsSource === 'webEnvironmentMaps') {
    return environmentMapOptions.value;
  }
  return [];
};

const handleMultipleFieldChange = (field, values) => {
  const catalog = gltfRenderTemplateConfig.catalogs[field.optionsCatalog] || {};
  renderForm[field.id] = enforceExclusiveCatalogGroups(
    renderForm[field.id] || [],
    values,
    catalog,
  );
};

const getInitialRenderForm = () => ({
  ...clone(gltfRenderTemplateConfig.defaults),
  ...resolveFieldRuntimeDefaults(
    gltfRenderTemplateConfig.fields,
    {
      plan: props.plan,
      store: store.state,
    },
  ),
});

const applyMissingRuntimeDefaults = () => {
  if (!hasTemplateConfig.value) return;

  const runtimeDefaults = resolveFieldRuntimeDefaults(
    gltfRenderTemplateConfig.fields,
    {
      plan: props.plan,
      store: store.state,
    },
  );

  Object.entries(runtimeDefaults).forEach(([fieldId, value]) => {
    if (renderForm[fieldId] === null || renderForm[fieldId] === undefined) {
      renderForm[fieldId] = value;
    }
  });
};

const generateConfigText = () => JSON.stringify(
  getGeneratedConfig(),
  null,
  2,
);

const isConfigEqualToGenerated = (value) => {
  if (!hasTemplateConfig.value) return false;

  const parsedValue = parseJsonConfig(value);
  if (!parsedValue) return false;

  return stableStringify(parsedValue) === stableStringify(getGeneratedConfig());
};

const extractFormValuesFromConfig = (value) => {
  const parsedValue = parseJsonConfig(value);
  if (!parsedValue) return {};

  return extractGltfRenderTemplateParameters(
    getRenderTemplate(),
    parsedValue,
    gltfRenderTemplateConfig.catalogs,
  );
};

const emitConfig = () => {
  emit('update:modelValue', gltfConfigText.value);
};

const setConfigTextFromCode = (value, shouldEmit = false) => {
  programmaticConfigText.value = value;
  gltfConfigText.value = value;

  if (shouldEmit) {
    emitConfig();
  }
};

const applyGeneratedConfig = () => {
  if (!hasTemplateConfig.value) return;

  isApplyingGeneratedConfig.value = true;
  setConfigTextFromCode(generateConfigText(), true);
  nextTick(() => {
    isApplyingGeneratedConfig.value = false;
  });
};

const rebuildFromForm = () => {
  isManualMode.value = false;
  applyGeneratedConfig();
};

const isJsonValid = () => Boolean(parseJsonConfig(gltfConfigText.value));

const isEmptyFormValue = (value) => {
  if (Array.isArray(value)) return value.length === 0;
  return value === null || value === undefined || value === '';
};

const getFormValidationMessage = () => {
  if (!hasTemplateConfig.value || isManualMode.value) return null;

  const invalidSource = visibleFields.value.find((field) => getFileSourceSettings(field)
    && !isEmptyFormValue(renderForm[field.id]) && !getSelectedFileBase(field));
  if (invalidSource) return `Укажите корректный домен загрузки для «${invalidSource.label || invalidSource.id}»`;

  const validations = gltfRenderTemplateConfig.validations || [];

  const invalidValidation = validations.find((validation) => {
    if (!isGltfRenderConditionMatched(validation.when, renderForm)) {
      return false;
    }

    if (validation.type === 'required') {
      return isEmptyFormValue(renderForm[validation.field]);
    }

    return false;
  });

  return invalidValidation?.message || null;
};

const getValidationMessage = () => {
  if (hasJsonErrors.value || !isJsonValid()) {
    return 'Исправьте ошибки в JSON конфиге';
  }

  return getFormValidationMessage();
};

const emitErrors = () => {
  emit('update:hasErrors', Boolean(getValidationMessage()));
};

const handleEditorUpdate = (value) => {
  const isProgrammaticUpdate = value === programmaticConfigText.value;
  if (isProgrammaticUpdate) {
    programmaticConfigText.value = null;
  }

  gltfConfigText.value = value;

  if (!isProgrammaticUpdate && !isApplyingGeneratedConfig.value && !isResettingData.value) {
    isManualMode.value = hasTemplateConfig.value && !isConfigEqualToGenerated(value);
  }

  emitConfig();
  emitErrors();
};

const handleJsonErrorsUpdate = (value) => {
  hasJsonErrors.value = value;
  emitErrors();
};

const resetData = () => {
  isResettingData.value = true;

  if (!hasTemplateConfig.value) {
    setConfigTextFromCode(normalizeConfigToString(props.modelValue));
    nextTick(() => {
      isResettingData.value = false;
      emitErrors();
    });
    return;
  }

  Object.assign(renderForm, getInitialRenderForm());
  Object.keys(fileSourceSelections).forEach((key) => { delete fileSourceSelections[key]; });

  const savedConfig = normalizeConfigToString(props.modelValue);
  if (savedConfig) {
    Object.assign(renderForm, extractFormValuesFromConfig(savedConfig));
  }

  gltfRenderTemplateConfig.fields.forEach((field) => {
    const settings = getFileSourceSettings(field);
    if (!settings) return;
    const { backendBaseUrl, cdns, cdnPathPrefix } = settings;
    const restored = restoreDownloadSource(renderForm[field.id], backendBaseUrl, cdns, cdnPathPrefix);
    renderForm[field.id] = restored.value;
    fileSourceSelections[field.id] = {
      source: restored.source, customDomain: restored.customDomain,
    };
  });

  isManualMode.value = Boolean(savedConfig) && !isConfigEqualToGenerated(savedConfig);
  setConfigTextFromCode(savedConfig || generateConfigText());

  if (!savedConfig) {
    emitConfig();
  }

  nextTick(() => {
    isResettingData.value = false;
    emitErrors();
  });
};

const loadRuntimeSources = () => Promise.all([
  store.dispatch('substances/getStylesList'),
  store.dispatch('substances/getUnrealEnvironment'),
  store.dispatch('substances/getPresetsList'),
]);

watch(
  [renderForm, fileSourceSelections],
  () => {
    if (hasTemplateConfig.value && !isManualMode.value && !isResettingData.value) {
      applyGeneratedConfig();
      emitErrors();
    }
  },
  { deep: true },
);

watch(
  () => props.modelValue,
  (newValue) => {
    const normalizedValue = normalizeConfigToString(newValue);
    if (normalizedValue !== gltfConfigText.value) {
      resetData();
    }
  },
);

onMounted(() => {
  resetData();

  if (!hasTemplateConfig.value) return;

  loadRuntimeSources()
    .then(() => {
      applyMissingRuntimeDefaults();
      if (isManualMode.value) {
        emitErrors();
        return;
      }
      applyGeneratedConfig();
      emitErrors();
    })
    .catch(() => {
      emitErrors();
    });
});

defineExpose({
  getValidationMessage,
  validate: () => !getValidationMessage(),
});
</script>
